import {
  type AuthConfirmResetPasswordT,
  type AuthLoginCredentialsT,
  type AuthRegisterCredentialsT,
} from '@ruminati/types_v2/auth'
import {
  type MobileSignupCompleteRequestT,
  type MobileSignupCompleteResponseT,
  type MobileSignupStartRequestT,
  type MobileSignupStartResponseT,
  type MobileSignupVerifyRequestT,
  type MobileSignupVerifyResponseT,
} from '@ruminati/types_v2/mobile-signup'
import { type UserWithOrganisationsAndRelationshipsT } from '@ruminati/types_v2/user'
import { useMutation } from '@tanstack/vue-query'
import ky from 'ky'

import { BASE_URL, CommonApiV2Instance } from '@/services/common'

// Mailbox proof is independent of browser login. Invalid codes must not trigger the shared
// session-invalid broadcaster, and single-use proof requests must not retry automatically.
const signupApi = ky.create({ prefixUrl: BASE_URL, credentials: 'omit', retry: 0 })

async function registerWithCredentials(details: AuthRegisterCredentialsT) {
  return CommonApiV2Instance.post('auth/register', {
    json: details,
  }).json<UserWithOrganisationsAndRelationshipsT>()
}

async function loginWithCredentials(details: AuthLoginCredentialsT) {
  return CommonApiV2Instance.post('auth/login', {
    json: details,
  }).json<UserWithOrganisationsAndRelationshipsT>()
}

export async function startMobileSignup(details: MobileSignupStartRequestT): Promise<void> {
  await signupApi
    .post('mobile/auth/signup/start', {
      json: details,
    })
    .json<MobileSignupStartResponseT>()
}

export async function verifyMobileSignup(
  details: MobileSignupVerifyRequestT,
): Promise<MobileSignupVerifyResponseT> {
  return signupApi
    .post('mobile/auth/signup/verify', {
      json: details,
    })
    .json<MobileSignupVerifyResponseT>()
}

export async function completeMobileSignup(details: MobileSignupCompleteRequestT): Promise<void> {
  await signupApi
    .post('mobile/auth/signup/complete', {
      json: details,
    })
    .json<MobileSignupCompleteResponseT>()
}

async function sendVerificationEmail(details: { email: string }) {
  await CommonApiV2Instance.post('auth/verify-email', {
    json: details,
    headers: { 'content-type': undefined },
  })
}

async function sendForgotPasswordEmail(details: { email: string }) {
  await CommonApiV2Instance.post('auth/forgot-password', {
    json: details,
    headers: { 'content-type': undefined },
  })
}

async function confirmResetPassword(details: AuthConfirmResetPasswordT) {
  await CommonApiV2Instance.post('auth/confirm-reset-password', {
    json: details,
    headers: { 'content-type': undefined },
  })
}

export async function requestLogout() {
  await CommonApiV2Instance.post('auth/logout', {
    headers: { 'content-type': undefined },
  })
}

export function useRegisterWithCredentialsMutation() {
  return useMutation({ mutationFn: registerWithCredentials })
}

export function useLoginWithCredentialsMutation() {
  return useMutation({ mutationFn: loginWithCredentials })
}

export function useSendVerificationEmailMutation() {
  return useMutation({ mutationFn: sendVerificationEmail })
}

export function useSendForgotPasswordEmailMutation() {
  return useMutation({ mutationFn: sendForgotPasswordEmail })
}

export function useConfirmResetPasswordMutation() {
  return useMutation({ mutationFn: confirmResetPassword })
}

export function useLogoutMutation() {
  return useMutation({ mutationFn: requestLogout })
}
